import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Flag } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BLOCK_TYPES, describeBlock } from "./blockMeta";
import UnitInput from "@/components/app/UnitInput";
import { toCanonicalMeters, toCanonicalInches } from "@/lib/units";
import { goalOf, buildIntervalPayload } from "@/lib/intervals";
import IntervalResultsInput, { emptyIntervalRow } from "./IntervalResultsInput";

export default function BlockCompletionDialog({
  block, program, dayIdx, athleteId, onDone,
  // Controlled-open variant for the auto-prompt-from-timer flow:
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  prefill,
  hideTrigger,
  existingLogId,
  intervalRows,
  intervalUnit,
}) {
  const isIntervals = block.type === "intervals";
  const goal = goalOf(block.scheme_config);
  const freshRows = () => Array.from({ length: Math.max(1, Number(block.scheme_config?.rounds) || 1) }, emptyIntervalRow);
  const [ivlRows, setIvlRows] = useState(() => intervalRows || freshRows());
  const [ivlUnit, setIvlUnit] = useState(intervalUnit || block.scheme_config?.target_distance_unit || "m");
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (controlledOnOpenChange || (() => {})) : setInternalOpen;
  const [busy, setBusy] = useState(false);
  const emptyForm = {
    time_min: "",
    time_sec: "",
    rounds_completed: "",
    extra_reps: "",
    calories: "",
    distance: "",
    distance_unit: "m",
    height: "",
    height_unit: "in",
    rpe: "",
    notes: "",
  };
  const [form, setForm] = useState(emptyForm);

  // Apply prefill whenever it changes or the dialog opens — so the timer-driven
  // auto-prompt arrives with elapsed time + counter values already filled in.
  React.useEffect(() => {
    if (open && prefill) {
      setForm((cur) => ({ ...cur, ...prefill }));
    } else if (!open && !isControlled) {
      setForm(emptyForm);
      setIvlRows(freshRows());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill]);

  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const min = num(form.time_min) || 0;
      const sec = num(form.time_sec) || 0;
      const totalSeconds = (min || sec) ? (min * 60 + sec) : null;
      const ivl = isIntervals ? buildIntervalPayload(goal, ivlRows, ivlUnit) : null;
      if (isIntervals && !ivl.interval_results.length) {
        toast.error("Enter at least one interval result");
        return;
      }
      const payload = {
        total_time_seconds: totalSeconds,
        rounds_completed: num(form.rounds_completed),
        extra_reps: num(form.extra_reps),
        calories: num(form.calories),
        distance_meters: toCanonicalMeters(form.distance, form.distance_unit),
        distance_unit: form.distance_unit || "m",
        height_inches: toCanonicalInches(form.height, form.height_unit),
        height_unit: form.height_unit || "in",
        rpe: num(form.rpe),
        notes: form.notes || null,
        ...(ivl || {}),
      };
      if (existingLogId) {
        const keep = Object.fromEntries(Object.entries(ivl || {}).filter(([, v]) => v != null));
        await api.patch(`/workout-logs/${existingLogId}`, { rpe: payload.rpe, notes: payload.notes, ...keep });
      } else {
        await api.post("/workout-logs", {
          ...payload,
          program_id: program.id,
          day_of_week: dayIdx,
          set_number: 1,
          block_id: block.id,
          block_type: block.type,
          block_title: block.title || BLOCK_TYPES[block.type]?.label,
        });
      }
      toast.success("Result logged");
      setIvlRows(freshRows());
      setOpen(false);
      setForm(emptyForm);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const askTime = block.type === "metcon";
  const askRounds = block.type === "amrap" || block.type === "emom" || block.type === "circuit";
  const askReps = block.type === "amrap" || block.type === "tabata";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button className="w-full rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white h-11" data-testid={`complete-block-${block.id}`}>
            <Flag size={16} className="mr-2" weight="fill" /> Complete section
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Log result — {block.title || BLOCK_TYPES[block.type]?.label}</DialogTitle>
          <DialogDescription>{describeBlock(block) || ""}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="block-completion-form">
          {isIntervals && (
            <IntervalResultsInput goal={goal} rows={ivlRows} setRows={setIvlRows}
              distanceUnit={ivlUnit} setDistanceUnit={setIvlUnit} />
          )}
          {askTime && (
            <div>
              <Label className="hs-label">Total time</Label>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div>
                  <Input type="number" min="0" value={form.time_min} onChange={(e) => setForm({ ...form, time_min: e.target.value })} className="h-11 rounded-xl font-data" placeholder="min" data-testid="block-total-time-min" autoFocus />
                </div>
                <div>
                  <Input type="number" min="0" max="59" value={form.time_sec} onChange={(e) => setForm({ ...form, time_sec: e.target.value })} className="h-11 rounded-xl font-data" placeholder="sec" data-testid="block-total-time-sec" />
                </div>
              </div>
              <p className="text-xs text-ink-3 mt-1">e.g., 4 min 32 sec</p>
            </div>
          )}
          {askRounds && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="hs-label">Rounds completed</Label>
                <Input type="number" min="0" step="0.5" value={form.rounds_completed} onChange={(e) => setForm({ ...form, rounds_completed: e.target.value })} className="mt-2 h-11 rounded-xl font-data" data-testid="block-rounds-completed" />
              </div>
              {askReps && (
                <div>
                  <Label className="hs-label">+ Extra reps</Label>
                  <Input type="number" min="0" value={form.extra_reps} onChange={(e) => setForm({ ...form, extra_reps: e.target.value })} className="mt-2 h-11 rounded-xl font-data" data-testid="block-extra-reps" />
                </div>
              )}
            </div>
          )}
          {block.type === "tabata" && (
            <div>
              <Label className="hs-label">Total reps (all rounds)</Label>
              <Input type="number" min="0" value={form.extra_reps} onChange={(e) => setForm({ ...form, extra_reps: e.target.value })} className="mt-2 h-11 rounded-xl font-data" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className={isIntervals ? "hidden" : ""}>
              <Label className="hs-label">Calories</Label>
              <Input type="number" min="0" value={form.calories} onChange={(e) => setForm({ ...form, calories: e.target.value })} className="mt-2 h-11 rounded-xl font-data" />
            </div>
            <div>
              <Label className="hs-label">RPE</Label>
              <Input type="number" step="0.5" min="0" max="10" value={form.rpe} onChange={(e) => setForm({ ...form, rpe: e.target.value })} className="mt-2 h-11 rounded-xl font-data" />
            </div>
          </div>
          <div>
            <Label className="hs-label">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 rounded-xl" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white" data-testid="submit-block-completion">{busy ? "Saving…" : "Log result"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
